import React from 'react';
import {
  Box,
  ListItem,
  ListItemText,
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core';
import { BlackHeadings, GraySmall, NonHoverBackground } from 'src/theme/colors';
import { typography13MediumStyle } from 'src/components/Text';
import { TruncatedText } from 'src/components/UI';

import { UserAvatar } from 'src/components/User';
import BaseTypography, {
  typography13RegularStyle,
} from 'src/components/Text/BaseTypography';
import { GrayThinBorder } from 'src/theme/borders';
import { ItemRenderProps } from 'src/components/Channels/BaseChannelSidebar';
import {
  InboxNotificationStatus,
  InboxSidebarItemData,
} from 'src/components/Inbox/InboxSidebar';
import { InboxPageContext } from 'src/context/inboxPageContext';
import { useUpdateInboxNotificationStatusMutation } from 'src/services/api/inboxApi';

interface InboxSidebarItemProps extends ItemRenderProps<InboxSidebarItemData> {
  style: React.CSSProperties;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentItem: {
      height: 60,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1.6, 4.5),
      color: theme.palette.primary.main,
      transition: '0.15s',
      cursor: 'pointer',
      borderBottom: GrayThinBorder,
      [theme.breakpoints.down('xs')]: {
        maxWidth: 'initial',
        padding: theme.spacing(1, 2),
      },
      [theme.breakpoints.up('sm')]: {
        '&:hover': {
          backgroundColor: NonHoverBackground,
        },
      },
    },
    selectedItem: {
      '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: NonHoverBackground,
      },
    },
    time: {
      color: GraySmall,
      lineHeight: 'inherit',
    },
    notificationCount: {
      float: 'right',
    },
    ellipse: {
      height: 8,
      minWidth: 8,
      background: theme.palette.primary.main,
      borderRadius: '50%',
    },
    itemText: {
      margin: 0,
    },
  }),
);

export const InboxSidebarItem = (props: InboxSidebarItemProps) => {
  const { selectedNotification } = React.useContext(InboxPageContext);
  const { channelOption, onSelect, style } = props;
  const [markNotificationAsRead] = useUpdateInboxNotificationStatusMutation();

  const classes = useStyles();

  const {
    avatarInfo,
    label,
    notificationStatus,
    description,
    id: notificationId,
  } = channelOption;

  const isNotificationUnread =
    notificationStatus === InboxNotificationStatus.Sent;

  const handleChannelClicked = () => {
    if (isNotificationUnread) {
      markNotificationAsRead({
        groupID: notificationId,
        status: '2_READ', // read notification status
      });
    }
    onSelect(channelOption);
  };

  const notificationLabelStyles = isNotificationUnread
    ? typography13MediumStyle
    : typography13RegularStyle;

  return (
    <ListItem
      style={{
        ...style,
      }}
      className={classes.contentItem}
      classes={{ selected: classes.selectedItem }}
      selected={selectedNotification?.id === notificationId}
      onClick={handleChannelClicked}
      key={`inbox-${notificationId}`}
      data-testid="inbox-sidebar-item"
    >
      <Box mr={1.5} position="relative" height={32} width={32}>
        {avatarInfo && (
          <Box key={avatarInfo.name}>
            <UserAvatar
              type="image"
              initialLetters={avatarInfo.name}
              name={avatarInfo.name}
              showName={false}
              avatarUrl={avatarInfo.imageUrl}
              fallbackColor={avatarInfo.fallbackColor}
              avatarStyles={{ borderRadius: '30px' }}
              avatarSize="xsmall"
            />
          </Box>
        )}
      </Box>

      <ListItemText
        className={classes.itemText}
        primary={
          <TruncatedText
            styleProps={{
              ...notificationLabelStyles,
              color: BlackHeadings,
            }}
            text={label}
          />
        }
        secondary={
          <BaseTypography
            className={classes.time}
            fontType={isNotificationUnread ? '13Medium' : '13Regular'}
          >
            {description}
          </BaseTypography>
        }
      />
      {isNotificationUnread && (
        <div className={classes.ellipse} data-testid="unread-notification" />
      )}
    </ListItem>
  );
};
